import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../middleware/dataSlice';
import {
  MakerChecker,
  MakerCheckerItem,
  ProcessType,
  StatusType,
  Iban,
} from '../../../types/approval.types';

export interface MakerCheckerListResponse {
  data: MakerCheckerItem[];
  totalCount: number;
  summary?: any;
  groupCount?: number;
}

export interface Fast {
  currencyId: number;
  value: number;
  description: string;
  valueFee: number;
  receiverIBAN?: string;
  receiverNameSurname?: string;
  receiverIdNo?: string | null;
  paymentPurpose?: string;
  companyId: number;
  customerId: number;
  isChecker: boolean;
}

export interface SendMoney {
  currencyId: string;
  value: string;
  receiverWalletNo?: string;
  description: string;
}

export interface SendMoneyApi {
  data: SendMoney[];
  companyId: number;
  customerId: number;
  isChecker: boolean;
}

export interface Withdrawal {
  currencyId: number;
  value: number;
  description: string;
  valueFee: number;
  ibanId: number;
}

export interface WithdrawalApi {
  data: Withdrawal;
  userId: string;
  companyId: number;
  customerId: number;
  isChecker: boolean;
}

export const approvalAPI = createApi({
  reducerPath: 'approvalAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MakerChecker', 'ProcessType', 'StatusType', 'Iban'],
  endpoints: (builder) => ({

    updateMakerChecker: builder.mutation<any, MakerChecker>({
      query: (data) => ({
        url: '/MakerChecker/UpdateMakerChecker',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['MakerChecker'],
    }),

    getProcessTypeLookup: builder.query<ProcessType[], void>({
      query: () => '/Process/ProcessTypeLookup',
      providesTags: ['ProcessType'],
    }),

    getMakerCheckerStatusLookup: builder.query<{ data: { value: number; text: string }[] }, void>({
      query: () => '/MakerChecker/MakerCheckerStatusLookup',
      providesTags: ['StatusType'],
      transformResponse: (response: any) => ({
        data: response.data,
      }),
    }),

    getIbanById: builder.query<{ data: Iban }, number>({
      query: (ibanId) => `/Company/GetIbanById?ibanId=${ibanId}`,
      providesTags: ['Iban'],
    }),

    sendMoney: builder.mutation<any, SendMoneyApi>({
      query: (data) => ({
        url: '/process/SendMoney',
        method: 'POST',
        body: data,
      }),
    }),

    withdrawal: builder.mutation<any, WithdrawalApi>({
      query: (data) => ({
        url: '/process/Withdrawal',
        method: 'POST',
        body: data,
      }),
    }),

    sendMoneyList: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendMoneyList',
        method: 'POST',
        body: data,
      }),
    }),
    sendMoneyPhoneNumberList: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendMoneyPhoneNumberList',
        method: 'POST',
        body: data,
      }),
    }),
    sendMealAllowanceList: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendMealAllowanceList',
        method: 'POST',
        body: data,
      }),
    }),
    sendMealAllowanceListWithWalletNo: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendMealAllowanceListWithWalletNo',
        method: 'POST',
        body: data,
      }),
    }),
    SendCashbackList: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendCashbackList',
        method: 'POST',
        body: data,
      }),
    }),
    sendMoneyMealAllowanceList: builder.mutation<any, any>({
      query: (data) => ({
        url: '/process/SendMoneyMealAllowanceList',
        method: 'POST',
        body: data,
      }),
    }),

  }),
});

export const {
  useUpdateMakerCheckerMutation,
  useGetProcessTypeLookupQuery,
  useGetMakerCheckerStatusLookupQuery,
  useGetIbanByIdQuery,
  useSendMoneyMutation,
  useWithdrawalMutation,
  useSendMoneyListMutation,
  useSendMoneyPhoneNumberListMutation,
  useSendMealAllowanceListMutation,
  useSendMealAllowanceListWithWalletNoMutation,
  useSendCashbackListMutation,
  useSendMoneyMealAllowanceListMutation,
} = approvalAPI;
